import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userPreferencesService, UserPreferences } from '@/services/userPreferencesService';
import { useAuth } from '@/contexts/AuthContext';

export const userPreferencesKeys = {
  all: ['userPreferences'] as const,
  // Preferences belong to a user. "Run as user" changes identity in-app, without a reload, so the
  // key carries the identity: the impersonated user's preferences are read, never the cached
  // impersonator's.
  forUser: (userId: string | undefined) => [...userPreferencesKeys.all, userId ?? ''] as const,
};

// Each tab reads the preferences once and never again (staleTime: Infinity), so a change saved in
// one tab would stay invisible to the others until they reload — and the next save from a stale
// tab would write its old value back over it. A saved change says so here; the other tabs of this
// browser read again. One instance per tab posts and listens, so a tab never hears its own save.
// Carries no values, only the signal.
export const USER_PREFERENCES_CHANNEL = 'nexo-user-preferences';
const preferencesChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(USER_PREFERENCES_CHANNEL) : null;

export function useUserPreferencesQuery() {
  const { user } = useAuth();
  return useQuery({
    queryKey: userPreferencesKeys.forUser(user?.sub),
    queryFn: () => userPreferencesService.get(),
    staleTime: Infinity,
  });
}

/** Re-reads the preferences when another tab of this browser saved a change. Mounted once. */
export function useUserPreferencesTabSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!preferencesChannel) return undefined;
    const reread = () => queryClient.invalidateQueries({ queryKey: userPreferencesKeys.all });
    preferencesChannel.addEventListener('message', reread);
    return () => preferencesChannel.removeEventListener('message', reread);
  }, [queryClient]);
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = userPreferencesKeys.forUser(user?.sub);

  return useMutation({
    // A save carries a whole value (the favorites are one list), so two saves arriving out of
    // order would leave the older one standing. Saves run one at a time, in the order made.
    scope: { id: 'user-preferences' },
    mutationFn: (settings: Partial<Record<string, string>>) =>
      userPreferencesService.update(settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<UserPreferences>(queryKey);

      queryClient.setQueryData<UserPreferences>(queryKey, (old) =>
        old ? { ...old, ...settings } as UserPreferences : old,
      );

      return { previous };
    },
    onSuccess: () => {
      preferencesChannel?.postMessage({ type: 'changed' });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
